import { FC, PropsWithChildren, useEffect, useState } from "react";
import { Page as CorePage, PageProps as CorePageProps } from "am-tax-fe-core";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiKeyPopup } from "../components/ApiKeyPopup";
import { CreateApiKeyModal } from "../components/CreateApiKeyModal";
import { Box, Image, useColorModeValue } from "@chakra-ui/react";
import ambientLogoLight from "../assets/images/ambient_logo_color.png";
import ambientLogoDark from "../assets/images/ambient_logo_white.png";
import { isThereApiKeyParam, useApiKeyAction } from "../hooks/useApiKeyAction";
import { useCreateApiKeyAction } from "../hooks/useCreateApiKeyAction";
import { isSharepointManaged, isSharepointManagedSet } from "../state/useAppState";
import { Loading } from "../components/Loading";
import { settings } from "../utils/settings";

export type PageProps = PropsWithChildren<Omit<CorePageProps, "location" | "menuItems">>;

type AmbientPageProps = PageProps & {
    requireApiKey?: boolean;
    allowApiKeyPopupDismiss?: boolean;
};

export const Page: FC<AmbientPageProps> = ({ children, requireApiKey = true, allowApiKeyPopupDismiss = false, ...pageProps }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [subTitle, setSubTitle] = useState(isSharepointManaged() ? "SharePoint-managed" : "");
    const logoSrc = useColorModeValue(ambientLogoLight, ambientLogoDark);
    const [isLoading, setIsLoading] = useState(requireApiKey);

    const subTitleCallback = () => {
        setSubTitle(isSharepointManaged() ? "SharePoint-managed" : "");
        setIsLoading(false);
    };
    const { showApiKeyPopup, handleCloseApiKeyPopup, apiKeyAction } = useApiKeyAction({
        callback: requireApiKey ? subTitleCallback : undefined,
        enabled: requireApiKey,
    });
    const { showCreateApiKeyModal, handleCloseCreateApiKeyModal, redirectToAdminPage: redirectToAdminPage } = useCreateApiKeyAction();
    const defaultLogo = (
        <Box cursor="pointer" onClick={() => navigate("/application")}>
            <Image src={logoSrc} height="2.5rem" alt="Ambient AI Logo" />
        </Box>
    );
    let actions = [...(pageProps.topNavProps?.actions || [])];

    if (requireApiKey) {
        if (isThereApiKeyParam()) {
            actions = actions.filter(action => action.label !== "Input an API key");
        } else {
            actions.push(apiKeyAction);
        }
    }

    // Add Admin link for admin users
    if (redirectToAdminPage) {
        actions.push(redirectToAdminPage);
    }

    useEffect(() => {
        if (!requireApiKey) {
            setIsLoading(false);
            return;
        }

        if (isSharepointManagedSet()) {
            setIsLoading(false);
        }
    }, [requireApiKey]);

    return (
        <CorePage
            location={location}
            buildNumber={settings.VITE_BUILD_NUMBER}
            {...{
                ...pageProps,
                topNavProps: {
                    showLogo: true,
                    customLogo: defaultLogo,
                    minHeight: "auto",
                    textColor: "blue.300",
                    subTitle: subTitle,
                    title: "",
                    ...pageProps.topNavProps,
                    actions: actions,
                },
            }}
        >
            {isLoading ? <Loading /> : children}
            <ApiKeyPopup show={showApiKeyPopup} onClose={handleCloseApiKeyPopup} allowDismissWithoutApiKey={allowApiKeyPopupDismiss} />
            <CreateApiKeyModal show={showCreateApiKeyModal} onClose={handleCloseCreateApiKeyModal} />
        </CorePage>
    );
};
